import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Pipette, Hash, Grid3x3, History } from 'lucide-react';
import SVGColorBlock from '../SVGColorBlock';
import { rgbToHex, hexToRgb, rgbToHsv, hsvToRgb } from '../colorExpertUtils';
import {
	loadRecentColors,
	pushRecentColor,
	colorKey,
	PRESET_SWATCHES,
} from '../colorPickerStorage';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function cloneColor(c) {
	return { r: c.r, g: c.g, b: c.b };
}

function SwatchGrid({ colors, onPick, columns = 8 }) {
	if (!colors?.length) {
		return <p className="text-[10px] text-gray-400 py-1">None yet</p>;
	}
	return (
		<div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
			{colors.map((c) => (
				<button
					key={colorKey(c)}
					type="button"
					onClick={() => onPick(c)}
					className="aspect-square rounded border border-gray-200 dark:border-gray-600 hover:ring-2 hover:ring-[#880e4f]/40 overflow-hidden"
					title={rgbToHex(c)}
				>
					<span className="block w-full h-full" style={{ backgroundColor: rgbToHex(c) }} />
				</button>
			))}
		</div>
	);
}

function SceneColorPicker({
	color,
	onChange,
	label,
	size = 24,
	sceneColors = [],
	zIndex = 60,
}) {
	const hasEyedropper = typeof window !== 'undefined' && 'EyeDropper' in window;
	const [open, setOpen] = useState(false);
	const [tab, setTab] = useState('picker');
	const [recent, setRecent] = useState(() => loadRecentColors());
	const [pos, setPos] = useState({ top: 0, left: 0 });
	const [hsv, setHsv] = useState(() => rgbToHsv(color.r, color.g, color.b));
	const triggerRef = useRef(null);
	const popoverRef = useRef(null);
	const slRef = useRef(null);
	const hsvRef = useRef(hsv);
	const onChangeRef = useRef(onChange);
	// Tracks the last color key we accepted from props / emitted ourselves.
	// Local HSV is authoritative while picking — never re-derive it from RGB on
	// pointer-up (that round-trip is what made the cursor bounce).
	const appliedKeyRef = useRef(colorKey(color));
	const pendingKeyRef = useRef(null);

	hsvRef.current = hsv;
	onChangeRef.current = onChange;

	const liveRgb = useMemo(() => hsvToRgb(hsv.h, hsv.s, hsv.v), [hsv]);

	const emitColor = useCallback((rgb) => {
		pendingKeyRef.current = colorKey(rgb);
		onChangeRef.current(rgb);
	}, []);

	// Adopt external color changes only. Ignore echo/stale props for colors we just emitted.
	useEffect(() => {
		const key = colorKey(color);
		if (pendingKeyRef.current != null) {
			if (key === pendingKeyRef.current) {
				pendingKeyRef.current = null;
				appliedKeyRef.current = key;
				return;
			}
			// Prop still has the previous color (or another lagging value) — keep local HSV.
			if (key === appliedKeyRef.current) return;
			// Unrelated external update won the race — take it.
			pendingKeyRef.current = null;
		}
		if (key === appliedKeyRef.current) return;
		appliedKeyRef.current = key;
		setHsv(rgbToHsv(color.r, color.g, color.b));
	}, [color]);

	// First recent color that differs from the live selection — stays stable while dragging.
	const lastColor = useMemo(() => {
		const current = colorKey(liveRgb);
		return recent.find((c) => colorKey(c) !== current) || null;
	}, [recent, liveRgb]);

	const commitColor = useCallback((next, remember = true) => {
		const rgb = cloneColor(next);
		const nextHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
		hsvRef.current = nextHsv;
		setHsv(nextHsv);
		emitColor(rgb);
		if (remember) {
			setRecent(pushRecentColor(rgb));
		}
	}, [emitColor]);

	const applyHsv = useCallback((h, s, v, remember = false) => {
		const next = { h, s, v };
		hsvRef.current = next;
		setHsv(next);
		const rgb = hsvToRgb(h, s, v);
		emitColor(rgb);
		if (remember) {
			setRecent(pushRecentColor(rgb));
		}
	}, [emitColor]);

	const rememberCurrent = useCallback(() => {
		setRecent(pushRecentColor(hsvToRgb(hsvRef.current.h, hsvRef.current.s, hsvRef.current.v)));
	}, []);

	const openPicker = () => {
		const rect = triggerRef.current?.getBoundingClientRect();
		if (rect) {
			const width = 272;
			let left = rect.left;
			let top = rect.bottom + 6;
			if (left + width > window.innerWidth - 8) {
				left = window.innerWidth - width - 8;
			}
			if (top + 320 > window.innerHeight - 8) {
				top = Math.max(8, rect.top - 320 - 6);
			}
			setPos({ top, left });
		}
		const nextHsv = rgbToHsv(color.r, color.g, color.b);
		hsvRef.current = nextHsv;
		setHsv(nextHsv);
		pendingKeyRef.current = null;
		appliedKeyRef.current = colorKey(color);
		setRecent(loadRecentColors());
		setOpen(true);
	};

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') setOpen(false);
		};
		const onPointer = (e) => {
			if (
				popoverRef.current?.contains(e.target)
				|| triggerRef.current?.contains(e.target)
			) return;
			setOpen(false);
		};
		window.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onPointer);
		return () => {
			window.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onPointer);
		};
	}, [open]);

	const pickSv = useCallback((clientX, clientY) => {
		const el = slRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const x = clamp((clientX - rect.left) / rect.width, 0, 1);
		const y = clamp((clientY - rect.top) / rect.height, 0, 1);
		const { h } = hsvRef.current;
		applyHsv(h, x * 100, (1 - y) * 100, false);
	}, [applyHsv]);

	const tryEyedropper = async () => {
		if (!window.EyeDropper) return;
		try {
			const dropper = new window.EyeDropper();
			const { sRGBHex } = await dropper.open();
			commitColor(hexToRgb(sRGBHex));
		} catch {
			// user cancelled
		}
	};

	const tabBtn = (id, icon, text) => (
		<button
			type="button"
			onClick={() => setTab(id)}
			className={`flex-1 flex items-center justify-center gap-1 px-1 py-1.5 text-[10px] rounded-md transition-colors ${
				tab === id
					? 'bg-[#880e4f]/15 text-[#880e4f] font-medium'
					: 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
			}`}
		>
			{icon}{text}
		</button>
	);

	const inputClass = 'w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs font-mono';

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => (open ? setOpen(false) : openPicker())}
				className="rounded overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-[#880e4f]/40 focus:outline-none focus:ring-2 focus:ring-[#880e4f]/50"
				title={label ? `Edit ${label}` : 'Edit color'}
				aria-label={label ? `Edit color for ${label}` : 'Edit color'}
			>
				<SVGColorBlock size={size} data={[color]} />
			</button>

			{open && (
				<div
					ref={popoverRef}
					className="fixed w-[272px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-3 space-y-2.5"
					style={{ top: pos.top, left: pos.left, zIndex }}
				>
					<div className="flex items-center gap-2">
						<SVGColorBlock size={32} data={[liveRgb]} />
						<div className="flex-1 min-w-0">
							{label && (
								<div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{label}</div>
							)}
							<div className="text-xs font-mono text-gray-800 dark:text-gray-100">{rgbToHex(liveRgb).toUpperCase()}</div>
						</div>
						{hasEyedropper && (
							<button
								type="button"
								onClick={tryEyedropper}
								className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500"
								title="Eyedropper"
							>
								<Pipette size={14} />
							</button>
						)}
					</div>

					{lastColor && (
						<button
							type="button"
							onClick={() => commitColor(lastColor)}
							className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:border-[#880e4f]/40 hover:bg-[#880e4f]/5 text-left"
						>
							<History size={12} className="text-gray-400 shrink-0" />
							<span className="text-[10px] text-gray-500 shrink-0">Last used</span>
							<span className="w-5 h-5 rounded border border-gray-200 dark:border-gray-600 shrink-0" style={{ backgroundColor: rgbToHex(lastColor) }} />
							<span className="text-[10px] font-mono text-gray-600 dark:text-gray-300 truncate">{rgbToHex(lastColor).toUpperCase()}</span>
						</button>
					)}

					<div className="flex gap-1 p-0.5 rounded-lg bg-gray-50 dark:bg-gray-800/80">
						{tabBtn('picker', <Pipette size={11} />, 'Picker')}
						{tabBtn('values', <Hash size={11} />, 'Values')}
						{tabBtn('swatches', <Grid3x3 size={11} />, 'Swatches')}
					</div>

					{tab === 'picker' && (
						<div className="space-y-2">
							<div
								ref={slRef}
								className="relative h-32 rounded-lg cursor-crosshair touch-none border border-gray-200 dark:border-gray-700"
								style={{
									background: `
										linear-gradient(to top, #000, transparent),
										linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))
									`,
								}}
								onPointerDown={(e) => {
									e.preventDefault();
									slRef.current?.setPointerCapture(e.pointerId);
									pickSv(e.clientX, e.clientY);
								}}
								onPointerMove={(e) => {
									if (!slRef.current?.hasPointerCapture(e.pointerId)) return;
									pickSv(e.clientX, e.clientY);
								}}
								onPointerUp={(e) => {
									if (slRef.current?.hasPointerCapture(e.pointerId)) {
										slRef.current.releasePointerCapture(e.pointerId);
									}
									rememberCurrent();
								}}
								onPointerCancel={rememberCurrent}
							>
								<span
									className="absolute w-3.5 h-3.5 -mt-[7px] -ml-[7px] rounded-full border-2 border-white shadow pointer-events-none"
									style={{
										left: `${hsv.s}%`,
										top: `${100 - hsv.v}%`,
										boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
									}}
								/>
							</div>
							<div className="relative h-3 rounded-full border border-gray-200 dark:border-gray-700">
								<input
									type="range"
									min="0"
									max="360"
									value={Math.round(hsv.h)}
									onChange={(e) => applyHsv(Number(e.target.value), hsv.s, hsv.v, false)}
									onPointerUp={rememberCurrent}
									onKeyUp={rememberCurrent}
									className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
									aria-label="Hue"
								/>
								<div
									className="absolute inset-0 rounded-full pointer-events-none"
									style={{
										background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
									}}
								/>
							</div>
						</div>
					)}

					{tab === 'values' && (
						<div className="space-y-2">
							<div>
								<label className="text-[10px] text-gray-500 uppercase">Hex</label>
								<input
									className={inputClass}
									value={rgbToHex(liveRgb).replace('#', '').toUpperCase()}
									onChange={(e) => {
										const raw = e.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 6);
										if (raw.length === 6) commitColor(hexToRgb(`#${raw}`));
									}}
									spellCheck={false}
								/>
							</div>
							<div className="grid grid-cols-3 gap-1.5">
								{['r', 'g', 'b'].map((ch) => (
									<div key={ch}>
										<label className="text-[10px] text-gray-500 uppercase">{ch}</label>
										<input
											type="number"
											min="0"
											max="255"
											className={inputClass}
											value={liveRgb[ch]}
											onChange={(e) => {
												const v = clamp(parseInt(e.target.value, 10) || 0, 0, 255);
												commitColor({ ...liveRgb, [ch]: v });
											}}
										/>
									</div>
								))}
							</div>
						</div>
					)}

					{tab === 'swatches' && (
						<div className="space-y-2.5 max-h-52 overflow-auto pr-0.5">
							<div>
								<div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Recent</div>
								<SwatchGrid colors={recent} onPick={commitColor} />
							</div>
							{sceneColors.length > 0 && (
								<div>
									<div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">This scene</div>
									<SwatchGrid colors={sceneColors} onPick={commitColor} />
								</div>
							)}
							<div>
								<div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Presets</div>
								<SwatchGrid colors={PRESET_SWATCHES} onPick={commitColor} />
							</div>
						</div>
					)}
				</div>
			)}
		</>
	);
}

export default SceneColorPicker;
